// utils/icons.ts - Centralized Icon System

export interface IconConfig {
  emoji: string;
  text: string;
  unicode: string;
  description: string;
}

export type IconType =
  // Medical & Health
  | "stethoscope"
  | "thermometer"
  | "pill"
  | "syringe"
  | "heart"
  | "heartbeat"
  | "lungs"
  | "brain"
  | "bone"
  | "tooth"
  | "eye"
  | "ear"
  | "blood"

  // Charts & Analytics
  | "chart-bar"
  | "chart-line"
  | "chart-pie"
  | "trending-up"
  | "trending-down"
  | "analytics"
  | "statistics"

  // Lab & Testing
  | "lab"
  | "test-tube"
  | "microscope"
  | "dna"
  | "petri-dish"

  // Documents & Files
  | "document"
  | "file"
  | "folder"
  | "clipboard"
  | "notes"
  | "form"
  | "pdf"
  | "image"
  | "attachment"

  // Calendar & Time
  | "calendar"
  | "clock"
  | "timer"
  | "schedule"
  | "appointment"

  // Actions
  | "edit"
  | "delete"
  | "save"
  | "cancel"
  | "close"
  | "add"
  | "remove"
  | "search"
  | "filter"
  | "sort"
  | "refresh"
  | "download"
  | "upload"
  | "print"
  | "share"
  | "copy"

  // Status & Alerts
  | "success"
  | "error"
  | "warning"
  | "info"
  | "alert"
  | "critical"
  | "pending"
  | "completed"
  | "cancelled"
  | "active"
  | "inactive"

  // Navigation
  | "home"
  | "dashboard"
  | "patients"
  | "appointments"
  | "settings"
  | "profile"
  | "logout"
  | "menu"
  | "back"
  | "forward"
  | "up"
  | "down"

  // Communication
  | "phone"
  | "email"
  | "message"
  | "notification"
  | "bell"

  // Measurements
  | "scale"
  | "ruler"
  | "gauge"
  | "percentage"

  // Pain & Symptoms
  | "pain"
  | "sad"
  | "neutral"
  | "happy"

  // General
  | "user"
  | "users"
  | "location"
  | "building"
  | "hospital"
  | "emergency"
  | "ambulance"
  | "first-aid"
  | "payment";

const ICON_DEFINITIONS: Record<IconType, IconConfig> = {
  // Medical & Health
  stethoscope: {
    emoji: "🩺",
    text: "[MED]",
    unicode: "\\ud83e\\ude7a",
    description: "Medical/Stethoscope",
  },
  thermometer: {
    emoji: "🌡️",
    text: "[TEMP]",
    unicode: "\\ud83c\\udf21",
    description: "Temperature",
  },
  pill: {
    emoji: "💊",
    text: "[RX]",
    unicode: "\\ud83d\\udc8a",
    description: "Medication/Pill",
  },
  syringe: {
    emoji: "💉",
    text: "[INJ]",
    unicode: "\\ud83d\\udc89",
    description: "Injection/Syringe",
  },
  heart: {
    emoji: "🫀",
    text: "[HEART]",
    unicode: "\\ud83e\\udec0",
    description: "Heart (anatomical)",
  },
  heartbeat: {
    emoji: "💓",
    text: "[HR]",
    unicode: "\\ud83d\\udc93",
    description: "Heart Rate",
  },
  lungs: {
    emoji: "🫁",
    text: "[LUNG]",
    unicode: "\\ud83e\\udec1",
    description: "Lungs",
  },
  brain: {
    emoji: "🧠",
    text: "[BRAIN]",
    unicode: "\\ud83e\\udde0",
    description: "Brain",
  },
  bone: {
    emoji: "🦴",
    text: "[BONE]",
    unicode: "\\ud83e\\uddee",
    description: "Bone",
  },
  tooth: {
    emoji: "🦷",
    text: "[TOOTH]",
    unicode: "\\ud83e\\uddee",
    description: "Tooth",
  },
  eye: {
    emoji: "👁️",
    text: "[EYE]",
    unicode: "\\ud83d\\udc41",
    description: "Eye",
  },
  ear: {
    emoji: "👂",
    text: "[EAR]",
    unicode: "\\ud83d\\udc42",
    description: "Ear",
  },
  blood: {
    emoji: "🩸",
    text: "[BLOOD]",
    unicode: "\\ud83e\\ude78",
    description: "Blood",
  },

  // Charts & Analytics
  "chart-bar": {
    emoji: "📊",
    text: "[CHART]",
    unicode: "\\ud83d\\udcca",
    description: "Bar Chart",
  },
  "chart-line": {
    emoji: "📈",
    text: "[TREND]",
    unicode: "\\ud83d\\udcc8",
    description: "Line Chart",
  },
  "chart-pie": {
    emoji: "🥧",
    text: "[PIE]",
    unicode: "\\ud83e\\udd67",
    description: "Pie Chart",
  },
  "trending-up": {
    emoji: "📈",
    text: "[UP]",
    unicode: "\\ud83d\\udcc8",
    description: "Trending Up",
  },
  "trending-down": {
    emoji: "📉",
    text: "[DOWN]",
    unicode: "\\ud83d\\udcc9",
    description: "Trending Down",
  },
  analytics: {
    emoji: "📊",
    text: "[STATS]",
    unicode: "\\ud83d\\udcca",
    description: "Analytics",
  },
  statistics: {
    emoji: "📊",
    text: "[STATS]",
    unicode: "\\ud83d\\udcca",
    description: "Statistics",
  },

  // Lab & Testing
  lab: {
    emoji: "🧪",
    text: "[LAB]",
    unicode: "\\ud83e\\uddea",
    description: "Laboratory",
  },
  "test-tube": {
    emoji: "🧪",
    text: "[TEST]",
    unicode: "\\ud83e\\uddea",
    description: "Test Tube",
  },
  microscope: {
    emoji: "🔬",
    text: "[MICRO]",
    unicode: "\\ud83d\\udd2c",
    description: "Microscope",
  },
  dna: {
    emoji: "🧬",
    text: "[DNA]",
    unicode: "\\ud83e\\uddec",
    description: "DNA",
  },
  "petri-dish": {
    emoji: "🧫",
    text: "[CULTURE]",
    unicode: "\\ud83e\\uddeb",
    description: "Petri Dish",
  },

  // Documents & Files
  document: {
    emoji: "📄",
    text: "[DOC]",
    unicode: "\\ud83d\\udcc4",
    description: "Document",
  },
  file: {
    emoji: "📁",
    text: "[FILE]",
    unicode: "\\ud83d\\udcc1",
    description: "File",
  },
  folder: {
    emoji: "📂",
    text: "[FOLDER]",
    unicode: "\\ud83d\\udcc2",
    description: "Folder",
  },
  clipboard: {
    emoji: "📋",
    text: "[CLIP]",
    unicode: "\\ud83d\\udccb",
    description: "Clipboard",
  },
  notes: {
    emoji: "📝",
    text: "[NOTE]",
    unicode: "\\ud83d\\udcdd",
    description: "Notes",
  },
  form: {
    emoji: "📝",
    text: "[FORM]",
    unicode: "\\ud83d\\udcdd",
    description: "Form",
  },
  pdf: {
    emoji: "📄",
    text: "[PDF]",
    unicode: "\\ud83d\\udcc4",
    description: "PDF Document",
  },
  image: {
    emoji: "🖼️",
    text: "[IMG]",
    unicode: "\\ud83d\\uddbc",
    description: "Image",
  },
  attachment: {
    emoji: "📎",
    text: "[ATTACH]",
    unicode: "\\ud83d\\udcce",
    description: "Attachment",
  },

  // Calendar & Time
  calendar: {
    emoji: "📅",
    text: "[CAL]",
    unicode: "\\ud83d\\udcc5",
    description: "Calendar",
  },
  clock: {
    emoji: "🕐",
    text: "[TIME]",
    unicode: "\\ud83d\\udd50",
    description: "Clock",
  },
  timer: {
    emoji: "⏱️",
    text: "[TIMER]",
    unicode: "\\u23f1",
    description: "Timer",
  },
  schedule: {
    emoji: "📅",
    text: "[SCHED]",
    unicode: "\\ud83d\\udcc5",
    description: "Schedule",
  },
  appointment: {
    emoji: "📅",
    text: "[APPT]",
    unicode: "\\ud83d\\udcc5",
    description: "Appointment",
  },

  // Actions
  edit: {
    emoji: "✏️",
    text: "[EDIT]",
    unicode: "\\u270f",
    description: "Edit",
  },
  delete: {
    emoji: "🗑️",
    text: "[DEL]",
    unicode: "\\ud83d\\uddd1",
    description: "Delete",
  },
  save: {
    emoji: "💾",
    text: "[SAVE]",
    unicode: "\\ud83d\\udcbe",
    description: "Save",
  },
  cancel: {
    emoji: "❌",
    text: "[CANCEL]",
    unicode: "\\u274c",
    description: "Cancel",
  },
  close: { emoji: "✕", text: "[X]", unicode: "\\u2715", description: "Close" },
  add: { emoji: "➕", text: "[ADD]", unicode: "\\u2795", description: "Add" },
  remove: {
    emoji: "➖",
    text: "[REM]",
    unicode: "\\u2796",
    description: "Remove",
  },
  search: {
    emoji: "🔍",
    text: "[SEARCH]",
    unicode: "\\ud83d\\udd0d",
    description: "Search",
  },
  filter: {
    emoji: "🔽",
    text: "[FILTER]",
    unicode: "\\ud83d\\udd3d",
    description: "Filter",
  },
  sort: {
    emoji: "🔀",
    text: "[SORT]",
    unicode: "\\ud83d\\udd00",
    description: "Sort",
  },
  refresh: {
    emoji: "🔄",
    text: "[REFRESH]",
    unicode: "\\ud83d\\udd04",
    description: "Refresh",
  },
  download: {
    emoji: "⬇️",
    text: "[DOWN]",
    unicode: "\\u2b07",
    description: "Download",
  },
  upload: {
    emoji: "⬆️",
    text: "[UP]",
    unicode: "\\u2b06",
    description: "Upload",
  },
  print: {
    emoji: "🖨️",
    text: "[PRINT]",
    unicode: "\\ud83d\\udda8",
    description: "Print",
  },
  share: {
    emoji: "📤",
    text: "[SHARE]",
    unicode: "\\ud83d\\udce4",
    description: "Share",
  },
  copy: {
    emoji: "📋",
    text: "[COPY]",
    unicode: "\\ud83d\\udccb",
    description: "Copy",
  },

  // Status & Alerts
  success: {
    emoji: "✅",
    text: "[OK]",
    unicode: "\\u2705",
    description: "Success",
  },
  error: {
    emoji: "❌",
    text: "[ERROR]",
    unicode: "\\u274c",
    description: "Error",
  },
  warning: {
    emoji: "⚠️",
    text: "[WARN]",
    unicode: "\\u26a0",
    description: "Warning",
  },
  info: {
    emoji: "ℹ️",
    text: "[INFO]",
    unicode: "\\u2139",
    description: "Information",
  },
  alert: {
    emoji: "🚨",
    text: "[ALERT]",
    unicode: "\\ud83d\\udea8",
    description: "Alert",
  },
  critical: {
    emoji: "🚨",
    text: "[CRIT]",
    unicode: "\\ud83d\\udea8",
    description: "Critical",
  },
  pending: {
    emoji: "⏳",
    text: "[PEND]",
    unicode: "\\u23f3",
    description: "Pending",
  },
  completed: {
    emoji: "✅",
    text: "[DONE]",
    unicode: "\\u2705",
    description: "Completed",
  },
  cancelled: {
    emoji: "❌",
    text: "[CANC]",
    unicode: "\\u274c",
    description: "Cancelled",
  },
  active: {
    emoji: "🟢",
    text: "[ACT]",
    unicode: "\\ud83d\\udd35",
    description: "Active",
  },
  inactive: {
    emoji: "🔴",
    text: "[INACT]",
    unicode: "\\ud83d\\udd34",
    description: "Inactive",
  },

  // Navigation
  home: {
    emoji: "🏠",
    text: "[HOME]",
    unicode: "\\ud83c\\udfe0",
    description: "Home",
  },
  dashboard: {
    emoji: "📊",
    text: "[DASH]",
    unicode: "\\ud83d\\udcca",
    description: "Dashboard",
  },
  patients: {
    emoji: "👥",
    text: "[PAT]",
    unicode: "\\ud83d\\udc65",
    description: "Patients",
  },
  appointments: {
    emoji: "📅",
    text: "[APPT]",
    unicode: "\\ud83d\\udcc5",
    description: "Appointments",
  },
  settings: {
    emoji: "⚙️",
    text: "[SET]",
    unicode: "\\u2699",
    description: "Settings",
  },
  profile: {
    emoji: "👤",
    text: "[PROF]",
    unicode: "\\ud83d\\udc64",
    description: "Profile",
  },
  logout: {
    emoji: "🚪",
    text: "[OUT]",
    unicode: "\\ud83d\\udea6",
    description: "Logout",
  },
  menu: { emoji: "☰", text: "[MENU]", unicode: "\\u2630", description: "Menu" },
  back: {
    emoji: "⬅️",
    text: "[BACK]",
    unicode: "\\u2b05",
    description: "Back",
  },
  forward: {
    emoji: "➡️",
    text: "[FWD]",
    unicode: "\\u27a1",
    description: "Forward",
  },
  up: { emoji: "⬆️", text: "[UP]", unicode: "\\u2b06", description: "Up" },
  down: {
    emoji: "⬇️",
    text: "[DOWN]",
    unicode: "\\u2b07",
    description: "Down",
  },

  // Communication
  phone: {
    emoji: "📞",
    text: "[PHONE]",
    unicode: "\\ud83d\\udcde",
    description: "Phone",
  },
  email: {
    emoji: "📧",
    text: "[EMAIL]",
    unicode: "\\ud83d\\udce7",
    description: "Email",
  },
  message: {
    emoji: "💬",
    text: "[MSG]",
    unicode: "\\ud83d\\udcac",
    description: "Message",
  },
  notification: {
    emoji: "🔔",
    text: "[NOTIF]",
    unicode: "\\ud83d\\udd14",
    description: "Notification",
  },
  bell: {
    emoji: "🔔",
    text: "[BELL]",
    unicode: "\\ud83d\\udd14",
    description: "Bell",
  },

  // Measurements
  scale: {
    emoji: "⚖️",
    text: "[WT]",
    unicode: "\\u2696",
    description: "Scale/Weight",
  },
  ruler: {
    emoji: "📏",
    text: "[RULE]",
    unicode: "\\ud83d\\udccf",
    description: "Ruler",
  },
  gauge: {
    emoji: "⏲️",
    text: "[GAUGE]",
    unicode: "\\u23f2",
    description: "Gauge",
  },
  percentage: {
    emoji: "%",
    text: "[%]",
    unicode: "\\u0025",
    description: "Percentage",
  },

  // Pain & Symptoms
  pain: {
    emoji: "😣",
    text: "[PAIN]",
    unicode: "\\ud83d\\ude23",
    description: "Pain",
  },
  sad: {
    emoji: "😢",
    text: "[SAD]",
    unicode: "\\ud83d\\ude22",
    description: "Sad",
  },
  neutral: {
    emoji: "😐",
    text: "[NEU]",
    unicode: "\\ud83d\\ude10",
    description: "Neutral",
  },
  happy: {
    emoji: "😊",
    text: "[HAPPY]",
    unicode: "\\ud83d\\ude0a",
    description: "Happy",
  },

  // General
  user: {
    emoji: "👤",
    text: "[USER]",
    unicode: "\\ud83d\\udc64",
    description: "User",
  },
  users: {
    emoji: "👥",
    text: "[USERS]",
    unicode: "\\ud83d\\udc65",
    description: "Users",
  },
  location: {
    emoji: "📍",
    text: "[LOC]",
    unicode: "\\ud83d\\udccd",
    description: "Location",
  },
  building: {
    emoji: "🏢",
    text: "[BLDG]",
    unicode: "\\ud83c\\udfe2",
    description: "Building",
  },
  hospital: {
    emoji: "🏥",
    text: "[HOSP]",
    unicode: "\\ud83c\\udfe5",
    description: "Hospital",
  },
  emergency: {
    emoji: "🚨",
    text: "[EMRG]",
    unicode: "\\ud83d\\udea8",
    description: "Emergency",
  },
  ambulance: {
    emoji: "🚑",
    text: "[AMB]",
    unicode: "\\ud83d\\ude91",
    description: "Ambulance",
  },
  "first-aid": {
    emoji: "🩹",
    text: "[AID]",
    unicode: "\\ud83e\\ude79",
    description: "First Aid",
  },
  payment: {
    emoji: "💲",
    text: "[PAY]",
    unicode: "\\ud83d\\udcb2",
    description: "Payment/Money",
  },
};

// Icon display modes
export type IconMode = "emoji" | "text" | "unicode" | "auto";

// Settings for icon mode (can be stored in localStorage/context)
let currentIconMode: IconMode = "auto";

export const setIconMode = (mode: IconMode): void => {
  currentIconMode = mode;
  localStorage.setItem("iconMode", mode);
};

export const getIconMode = (): IconMode => {
  if (typeof window !== "undefined") {
    const stored = localStorage.getItem("iconMode") as IconMode;
    if (stored) {
      currentIconMode = stored;
      return stored;
    }
  }
  return currentIconMode;
};

// Emoji support detection
export const supportsEmoji = (): boolean => {
  if (typeof window === "undefined") return false;

  try {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (!context) return false;

    context.textBaseline = "top";
    context.font = "16px Arial";
    context.fillText("🩺", 0, 0);

    const imageData = context.getImageData(0, 0, 16, 16).data;
    return Array.from(imageData).some((byte) => byte !== 0);
  } catch {
    return false;
  }
};

// Main icon function
export const getIcon = (iconType: IconType, mode?: IconMode): string => {
  const config = ICON_DEFINITIONS[iconType];
  if (!config) {
    console.warn(`Icon "${iconType}" not found`);
    return "[?]";
  }

  const displayMode = mode || getIconMode();

  switch (displayMode) {
    case "emoji":
      return config.emoji;
    case "text":
      return config.text;
    case "unicode":
      return config.unicode;
    case "auto":
    default:
      return supportsEmoji() ? config.emoji : config.text;
  }
};

// Utility functions
export const getIconWithLabel = (
  iconType: IconType,
  label: string,
  mode?: IconMode
): string => {
  return `${getIcon(iconType, mode)} ${label}`;
};

export const getIconConfig = (iconType: IconType): IconConfig | null => {
  return ICON_DEFINITIONS[iconType] || null;
};

export const getAllIcons = (): Record<IconType, IconConfig> => {
  return ICON_DEFINITIONS;
};

export const searchIcons = (query: string): IconType[] => {
  const lowerQuery = query.toLowerCase();
  return Object.keys(ICON_DEFINITIONS).filter(
    (iconType) =>
      iconType.includes(lowerQuery) ||
      ICON_DEFINITIONS[iconType as IconType].description
        .toLowerCase()
        .includes(lowerQuery)
  ) as IconType[];
};

// React component for easy usage (if you want)
export interface IconProps {
  type: IconType;
  mode?: IconMode;
  className?: string;
  style?: React.CSSProperties;
  title?: string;
}

// Default export for easy importing
export default getIcon;
